import { html, TemplateResult, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseComponent } from '../../base-component/base-component';
import { Modal } from '../../modal/modal';
import emit from '../../../common/events';
import { getAppFlow, getUserActiveTab } from '../../user-profile/utils/user-profile.util';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { getAnalyticsSafeData } from '../../user-profile/user-profile.selectors';

const ANALYTICS_COMPONENT = 'mhecomm-my-digital-products-v2';
const ANALYTICS_AREA = 'main';
const ANALYTICS_SUBAREA = 'body';
const HOW_TO_LAUNCH_EVENT = 'how-to-launch-connect';
const CLOSE_FOOTER_ID = 'how-to-launch-modal-close-footer';
const CLOSE_X_ID = 'how-to-launch-modal-close-x';

@customElement('mhecomm-connect-how-to-launch-modal')
export class ConnectHowToLaunchModal extends BaseComponent {
  @property()
  title = '';

  @property()
  coverImage = '';

  @property()
  altText = '';

  @property()
  isbn10 = '';

  @property()
  isbn13?: string;

  @property()
  orderId?: string;

  @property()
  editionOrRelease?: string;

  @property({ type: Boolean })
  isEvergreenPDP?: boolean;

  @property({ attribute: false })
  user?: UserProfile;

  private get _triggerAutomationId(): string {
    return `btn-launch-${this.orderId || ''}-${this.isbn10}`;
  }

  renderComp(): TemplateResult {
    return html`<button
      type="button"
      data-automation-id="${this._triggerAutomationId}"
      class="btn btn-primary w-100"
      @click=${(event: Event) => this._onTriggerClick(event)}
      aria-haspopup="dialog">
      How to Launch
    </button>`;
  }

  private _onTriggerClick(event: Event): void {
    this._emitHowToLaunchAnalytics();
    this._openHowToLaunchModal(event);
  }

  private _emitHowToLaunchAnalytics(): void {
    emit({
      element: this,
      name: HOW_TO_LAUNCH_EVENT,
      detail: JSON.parse(
        JSON.stringify({
          userRole: this.user?.persona?.role,
          userSegment: this.user?.persona?.segment,
          appFlow: getAppFlow(),
          buttonName: 'How to Launch',
          isbn10: this.isbn10,
          isbn13: this.isbn13,
          editionOrRelease: this.editionOrRelease,
          orderNumber: this.orderId,
          isEvergreenPDP: this.isEvergreenPDP,
          ...(getUserActiveTab() !== '' && { myAccountSection: getUserActiveTab() }),
          comp: ANALYTICS_COMPONENT,
          area: ANALYTICS_AREA,
          subarea: ANALYTICS_SUBAREA,
          id: this._triggerAutomationId,
        })
      ),
    });
  }

  private async _openHowToLaunchModal(event: Event): Promise<void> {
    const modal = new Modal({
      header: 'How to access your courseware',
      body: {
        template: this._renderModalBody(),
      },
      primaryButton: {
        label: 'Close',
      },
      modalContext: { componentRef: 'connect-how-to-launch-modal' },
      showClosingCross: true,
      isModalCentered: true,
    });

    modal.addEventListener('mhecomm-modal-confirmed', () => {
      this._emitCloseAnalytics(CLOSE_FOOTER_ID, 'Close');
      this._closeModal(modal);
    });
    this._attachDismissAnalytics(modal);
    await modal.open(event);
  }

  private _renderModalBody(): TemplateResult {
    return html`<div>
      <div class="bg-light p-3">
        <p>Access your courseware using one of the following options:</p>

        <p class="mb-1">Through your <strong>Learning Management System (LMS)</strong></p>
        <ul class="mb-3">
          <li>Log in through your LMS (like Blackboard, Canvas, or Moodle).</li>
          <li>Check your available course listing for this title.</li>
        </ul>

        <p class="mb-1">Through your <strong>course access link</strong></p>
        <ul class="mb-0">
          <li>If your course does not use an LMS, enter the Connect section URL provided by your instructor into your browser.</li>
          <li>If you haven't received it yet, contact your instructor for help.</li>
        </ul>
      </div>

      <div class="d-flex align-items-center mt-3">
        <div class="flex-shrink-0 me-3" style="width: 6rem;">
          <img alt=${this.altText || this.title} class="img-fluid" style="max-width: 96px;" src=${this.coverImage} />
        </div>
        <div class="flex-grow-1">
          <h4 class="h5 fs-large fw-semibold mb-1">${this.title}</h4>
          <div>
            <span class="fw-bold">ISBN10:</span> ${this.isbn10}
            ${this.isbn13 ? html` | <span class="fw-bold">ISBN13:</span> ${this.isbn13}` : nothing}
          </div>
          <div><span class="fw-bold">Format:</span> Connect</div>
        </div>
      </div>
    </div>`;
  }

  private _closeModal(modal: Modal): void {
    if ((modal as any).modalInstance) {
      modal.close(undefined, false);
      setTimeout(() => {
        if (document.body.contains(modal)) {
          modal.remove();
        }
      }, 0);
      return;
    }
    modal.remove();
  }

  private _attachDismissAnalytics(modal: Modal): void {
    modal.addEventListener(
      'click',
      (clickEvent: Event) => {
        const id = (clickEvent.composedPath()[0] as HTMLElement | undefined)?.id;
        if (id !== 'modalCloseButton') {
          return;
        }
        clickEvent.stopImmediatePropagation();
        this._emitCloseAnalytics(CLOSE_X_ID, 'Close');
        this._closeModal(modal);
      },
      { capture: true }
    );
  }

  private _emitCloseAnalytics(id: string, label: string): void {
    emit({
      element: this,
      name: 'click',
      detail: {
        label,
        comp: ANALYTICS_COMPONENT,
        area: ANALYTICS_AREA,
        subarea: ANALYTICS_SUBAREA,
        id,
        ...(this.user && { user: getAnalyticsSafeData(this.user) }),
        appFlow: getAppFlow(),
        ...(getUserActiveTab() !== '' && { myAccountSection: getUserActiveTab() }),
      },
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-connect-how-to-launch-modal': ConnectHowToLaunchModal;
  }
}
